/**
 * Décompte de participation collective d'UNE étape d'un défi (vrais défis).
 *
 * Le player ne peut pas lire les compteurs en direct (RLS `own_select` sur
 * live_participations / live_answers) → il poll la RPC security-definer
 * `get_live_step_participation` (migration 0197) qui renvoie uniquement des
 * agrégats + mon ordinal (aucun choix/corrigé exposé). En mode TEST, la
 * participation est SIMULÉE côté client (bots) → ce hook n'est pas utilisé.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// RPC postérieure aux types stale → client générique (comme les autres hooks live).
const db = supabase as unknown as SupabaseClient;

export interface StepParticipation {
  /** Participants réels censés jouer cette étape (moi inclus). */
  total: number;
  /** Nombre ayant déjà répondu à cette étape. */
  answered: number;
  /** Mon rang (1-based) parmi ceux ayant répondu, `null` si je n'ai pas répondu. */
  myPosition: number | null;
}

interface StepParticipationRow {
  total: number | null;
  answered: number | null;
  my_position: number | null;
}

/**
 * Poll (~1,5 s) le décompte de participation de l'étape courante d'un VRAI défi.
 * `enabled=false` en mode test (bots simulés) et hors phase LIVE.
 */
export function useLiveStepParticipation({
  challengeId,
  stepIndex,
  enabled,
}: {
  challengeId: string | null;
  stepIndex: number;
  enabled: boolean;
}) {
  return useQuery<StepParticipation>({
    queryKey: ['live-step-participation', challengeId, stepIndex],
    enabled: enabled && !!challengeId && stepIndex >= 0,
    refetchInterval: 1500,
    staleTime: 1000,
    queryFn: async (): Promise<StepParticipation> => {
      const { data, error } = await db.rpc('get_live_step_participation', {
        p_challenge_id: challengeId,
        p_step_index: stepIndex,
      });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as StepParticipationRow | undefined;
      return {
        total: Number(row?.total ?? 0),
        answered: Number(row?.answered ?? 0),
        myPosition: row?.my_position == null ? null : Number(row.my_position),
      };
    },
  });
}
